/**
 * EffectPresetsPanel
 *
 * Sidebar section displayed inside TextOverlayEditor when a text layer is
 * selected.  Shows a grid of 6 effect preset cards and, below it, the
 * parameter controls that are relevant to the currently-selected preset.
 */

import React from 'react';
import {
  EFFECT_PRESETS,
  DEFAULT_EFFECT_PARAMS,
  type EffectId,
  type EffectParams,
} from './presets';

// ─── Tiny colour-swatch preview for each preset ──────────────────────────────
// Shows a simple stylized text snippet so the user can see what the effect
// looks like at a glance.

const PRESET_ICONS: Record<EffectId, React.ReactNode> = {
  none: (
    <span className="font-bold text-sm" style={{ color: '#ffffff', textShadow: 'none' }}>
      Aa
    </span>
  ),
  gradient_fill: (
    <span
      className="font-bold text-sm"
      style={{
        background: 'linear-gradient(to bottom, #ffd700, #ff4500)',
        WebkitBackgroundClip: 'text',
        WebkitTextFillColor: 'transparent',
      }}
    >
      Aa
    </span>
  ),
  chrome_stroke: (
    <span
      className="font-bold text-sm"
      style={{
        color: '#fff',
        WebkitTextStroke: '1px #c0c0c0',
        textShadow: '2px 2px 3px rgba(0,0,0,0.6)',
      }}
    >
      Aa
    </span>
  ),
  extrude_3d: (
    <span
      className="font-bold text-sm"
      style={{
        color: '#fff',
        textShadow: '1px 1px #888, 2px 2px #888, 3px 3px #888',
      }}
    >
      Aa
    </span>
  ),
  image_through_text: (
    <span
      className="font-bold text-sm"
      style={{
        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
        WebkitBackgroundClip: 'text',
        WebkitTextFillColor: 'transparent',
        filter: 'saturate(1.4)',
      }}
    >
      Aa
    </span>
  ),
  motion_blur: (
    <span
      className="font-bold text-sm"
      style={{
        color: '#fff',
        textShadow: '-3px 0 rgba(255,255,255,0.3), 3px 0 rgba(255,255,255,0.3)',
      }}
    >
      Aa
    </span>
  ),
};

// ─── Component ────────────────────────────────────────────────────────────────

interface Props {
  selectedEffect: EffectId;
  effectParams: EffectParams;
  onEffectChange: (effect: EffectId) => void;
  onParamChange: (partial: Partial<EffectParams>) => void;
}

export const EffectPresetsPanel: React.FC<Props> = ({
  selectedEffect,
  effectParams,
  onEffectChange,
  onParamChange,
}) => {
  return (
    <div className="flex flex-col gap-3">
      <label className="block text-xs font-semibold text-ink-2 uppercase tracking-wide">
        Effect
      </label>

      {/* ── Preset grid ── */}
      <div className="grid grid-cols-3 gap-1.5">
        {EFFECT_PRESETS.map(preset => (
          <button
            key={preset.id}
            onClick={() => onEffectChange(preset.id)}
            title={preset.description}
            className={`flex flex-col items-center gap-1 p-2 rounded-lg border text-center
                        transition-colors cursor-pointer select-none
                        ${selectedEffect === preset.id
                          ? 'border-primary bg-primary/10 dark:bg-primary/20 ring-1 ring-primary'
                          : 'border-line hover:border-gray-400 bg-navy/60'
                        }`}
          >
            <div className="w-8 h-6 flex items-center justify-center shrink-0">
              {PRESET_ICONS[preset.id]}
            </div>
            <span className="text-[10px] leading-tight text-gray-200 font-medium">
              {preset.label}
            </span>
          </button>
        ))}
      </div>

      {/* ── Effect-specific params ── */}
      {selectedEffect === 'gradient_fill' && (
        <GradientParams params={effectParams} onChange={onParamChange} />
      )}
      {selectedEffect === 'chrome_stroke' && (
        <ChromeParams params={effectParams} onChange={onParamChange} />
      )}
      {selectedEffect === 'extrude_3d' && (
        <ExtrudeParams params={effectParams} onChange={onParamChange} />
      )}
      {selectedEffect === 'motion_blur' && (
        <MotionBlurParams params={effectParams} onChange={onParamChange} />
      )}
      {selectedEffect === 'image_through_text' && (
        <p className="text-xs text-ink-3 italic">
          The background image shows through the text shape.
        </p>
      )}
    </div>
  );
};

// ─── Param sub-sections ───────────────────────────────────────────────────────

interface ParamProps {
  params: EffectParams;
  onChange: (partial: Partial<EffectParams>) => void;
}

const ColorField: React.FC<{
  label: string;
  value: string;
  onChange: (v: string) => void;
}> = ({ label, value, onChange }) => (
  <div>
    <label className="block text-xs text-ink-2 mb-1">{label}</label>
    <div className="flex items-center gap-2">
      <input
        type="color"
        value={value}
        onChange={e => onChange(e.target.value)}
        className="w-8 h-7 cursor-pointer rounded border border-line-2 p-0.5 bg-card shrink-0"
      />
      <input
        type="text"
        value={value}
        onChange={e => {
          if (/^#[0-9a-fA-F]{0,6}$/.test(e.target.value)) onChange(e.target.value);
        }}
        maxLength={7}
        className="flex-1 text-xs border border-line rounded px-2 py-1
                   bg-card text-ink
                   focus:ring-1 focus:ring-primary focus:outline-none font-mono"
      />
    </div>
  </div>
);

const SliderField: React.FC<{
  label: string;
  value: number;
  min: number;
  max: number;
  step?: number;
  unit?: string;
  onChange: (v: number) => void;
}> = ({ label, value, min, max, step = 1, unit = '', onChange }) => (
  <div>
    <label className="block text-xs text-ink-2 mb-1">
      {label}: {value}{unit}
    </label>
    <input
      type="range"
      min={min}
      max={max}
      step={step}
      value={value}
      onChange={e => onChange(Number(e.target.value))}
      className="w-full accent-primary"
    />
  </div>
);

function GradientParams({ params, onChange }: ParamProps) {
  return (
    <div className="flex flex-col gap-2 p-2 bg-inset rounded-lg">
      <ColorField
        label="Top colour"
        value={params.gradientTopColor}
        onChange={v => onChange({ gradientTopColor: v })}
      />
      <ColorField
        label="Bottom colour"
        value={params.gradientBottomColor}
        onChange={v => onChange({ gradientBottomColor: v })}
      />
    </div>
  );
}

function ChromeParams({ params, onChange }: ParamProps) {
  return (
    <div className="flex flex-col gap-2 p-2 bg-inset rounded-lg">
      <ColorField
        label="Stroke colour"
        value={params.strokeColor}
        onChange={v => onChange({ strokeColor: v })}
      />
      <SliderField
        label="Stroke width"
        value={params.strokeWidth}
        min={1}
        max={10}
        unit="px"
        onChange={v => onChange({ strokeWidth: v })}
      />
      <SliderField
        label="Shadow depth"
        value={params.shadowOffset}
        min={0}
        max={12}
        unit="px"
        onChange={v => onChange({ shadowOffset: v })}
      />
    </div>
  );
}

function ExtrudeParams({ params, onChange }: ParamProps) {
  return (
    <div className="flex flex-col gap-2 p-2 bg-inset rounded-lg">
      <SliderField
        label="Depth"
        value={params.extrudeDepth}
        min={2}
        max={14}
        unit="px"
        onChange={v => onChange({ extrudeDepth: v })}
      />
      <ColorField
        label="Extrude colour"
        value={params.extrudeColor}
        onChange={v => onChange({ extrudeColor: v })}
      />
    </div>
  );
}

function MotionBlurParams({ params, onChange }: ParamProps) {
  return (
    <div className="flex flex-col gap-2 p-2 bg-inset rounded-lg">
      <SliderField
        label="Blur amount"
        value={params.blurAmount}
        min={4}
        max={30}
        unit="px"
        onChange={v => onChange({ blurAmount: v })}
      />
    </div>
  );
}

export { DEFAULT_EFFECT_PARAMS };
export type { EffectId, EffectParams };
